import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Building2, ChevronRight } from "lucide-react";
import logo from "@/assets/logo.png";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { sanitizeError } from "@/lib/errorUtils";
import { AppShell } from "@/components/layout/AppShell";
import { PulseLine, PulseLineSkeleton } from "@/components/animations/PulseLine";
import { cn } from "@/lib/utils";

interface Company {
  id: string;
  name: string;
  created_at: string;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState<string>("");

  useEffect(() => {
    loadCompanies();
    loadUserProfile();
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data: profile } = await supabase
      .from("profiles")
      .select("name")
      .eq("id", user.id)
      .single();
    if (!profile?.name) {
      navigate("/app/onboarding");
    }
  };

  const loadUserProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("name")
        .eq("id", user.id)
        .single();
      if (profile?.name) {
        setUserName(profile.name.split(" ")[0]);
      }
    }
  };

  const loadCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from("companies")
        .select("id, name, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setCompanies(data || []);
      
      // Check invitation redirect
      const redirect = sessionStorage.getItem("invitationRedirect");
      if (redirect) {
        sessionStorage.removeItem("invitationRedirect");
        try {
          const url = new URL(redirect);
          if (url.origin === window.location.origin) {
            window.location.href = redirect;
          }
        } catch { /* ignore */ }
      }
    } catch (error: unknown) {
      toast.error(sanitizeError(error, "Failed to load companies"));
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <AppShell>
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Welcome */}
          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              {getGreeting()}{userName ? `, ${userName}` : ""} 👋
            </h1>
            <p className="text-muted-foreground">
              {companies.length === 0 
                ? "Let's get started by creating your first company"
                : "Here's an overview of your companies"}
            </p>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => navigate("/app/companies/new")} 
              className="rounded-2xl shadow-md hover:shadow-lg transition-all bg-gradient-accent"
            >
              <Plus className="h-4 w-4 mr-2" />
              New company
            </Button>
            {companies.length > 0 && (
              <Button 
                variant="outline" 
                className="rounded-2xl border-border/50"
                onClick={() => {
                  const firstCompany = companies[0];
                  if (firstCompany) navigate(`/app/companies/${firstCompany.id}`);
                }}
              >
                <img src={logo} alt="Memo" className="h-4 w-4 rounded mr-2" />
                Continue with {companies[0]?.name}
              </Button>
            )}
          </div>

          {/* Companies */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="rounded-2xl">
                  <CardHeader className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-muted animate-pulse" />
                      <div className="space-y-2 flex-1">
                        <div className="h-5 bg-muted rounded-lg w-1/3 animate-pulse" />
                        <div className="h-3 bg-muted rounded-lg w-1/4 animate-pulse" />
                      </div>
                    </div>
                    <PulseLineSkeleton className="h-6" />
                  </CardHeader>
                </Card>
              ))}
            </div>
          ) : companies.length === 0 ? (
            <Card className="rounded-3xl border-dashed border-2 bg-card/50">
              <CardContent className="py-16 text-center">
                <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                  <Building2 className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-2">No companies yet</h3>
                <p className="text-muted-foreground mb-6 max-w-xs mx-auto text-sm">
                  Tell Memo your company name to start tracking, or create one manually.
                </p>
                {/* Empty state pulse line */}
                <div className="max-w-xs mx-auto mb-6 opacity-30">
                  <PulseLine color="primary" height={24} points={[0.5, 0.5, 0.5, 0.5, 0.5, 0.5]} animate={false} />
                </div>
                <Button 
                  onClick={() => navigate("/app/companies/new")}
                  className="rounded-2xl shadow-md bg-gradient-accent"
                  size="lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Start the pulse
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {companies.map((company, index) => (
                <Card
                  key={company.id}
                  className={cn(
                    "cursor-pointer rounded-2xl border-border/50 transition-all duration-300",
                    "hover:shadow-lg hover:border-primary/20 hover:bg-card-hover",
                    "active:scale-[0.99]"
                  )}
                  onClick={() => navigate(`/app/companies/${company.id}`)}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <CardHeader className="p-5 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-gradient-premium flex items-center justify-center shadow-sm">
                          <Building2 className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <CardTitle className="text-lg font-semibold">
                            {company.name}
                          </CardTitle>
                          <CardDescription className="text-sm">
                            Created {new Date(company.created_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric"
                            })}
                          </CardDescription>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
};

export default Dashboard;
